import type { SubTemplateValueSetupInOut } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, notEmpty } from '@festina-life/core'
import { useMemo } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import type { SubTemplateFieldValue } from '../../types/templateModel'
import { TemplatePathParts } from '../../uiModels/templateOperations/helpers'
import { useTemplateOperationsWithVersionInfo } from '../../uiModels/templateOperations/useTemplateOperationsWithVersionInfo'
import { useTemplateHierarchy } from '../../uiModels/versionInfo/useTemplateHierarchy'
import { isEqualOrSuperType, valueAtPath } from './helpers'

export const useSubTemplateParentCandidates = (
  variant: SubTemplateFieldValue,
) => {
  const { templateVersion } = useTemplateContext()
  const { subTemplatedField } = variant
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)

  assertNotEmpty(templateVersion)
  assertNotEmpty(subTemplatedField)

  const hierarchy = useTemplateHierarchy({
    forTemplate: templateVersion.versionInfo,
    withReplacements: true,
  })

  const createOperations = useTemplateOperationsWithVersionInfo(hierarchy)

  const availableSiblings = (subTemplatedField.value?.variants ?? []).filter(
    (fieldVariant) =>
      fieldVariant.subTemplateIdentifier !== variant.subTemplateIdentifier,
  )

  const variantParts = subTemplatedField.path.match(TemplatePathParts)?.groups
  const intermediatePath = variantParts?.['intermediatePath']

  assertNotEmpty(intermediatePath)

  const inheritedFieldsWithVariants = useMemo(
    () =>
      createOperations
        .slice(0, createOperations.length - 1)
        .filter(notEmpty)
        .map((createOperation) =>
          valueAtPath<SubTemplateValueSetupInOut>(
            createOperation,
            `{${createOperation.versionInfo.identifier}}${intermediatePath}`,
          ),
        ),
    [createOperations, intermediatePath],
  )

  const variantTypeName = variant.typeName

  return useMemo(() => {
    const parentOptions = new Set<string>()

    availableSiblings.forEach((siblingVariant) => {
      if (
        variantTypeName === variant.typeName ||
        isEqualOrSuperType({
          evaluatedTypeName: variantTypeName,
          modelMetaTypeName: siblingVariant.typeName,
          metaModel,
        })
      ) {
        parentOptions.add(siblingVariant.subTemplateIdentifier)
      }
    })

    inheritedFieldsWithVariants
      .filter(notEmpty)
      .flatMap((field) => field.variants)
      .filter(notEmpty)
      .filter(
        (inheritedVariant) =>
          inheritedVariant.name !== variant.subTemplateIdentifier,
      )
      .forEach((variant) => {
        assertNotEmpty(variant.templateTypeName)

        const inheritedVariantType = metaModel.types.get(
          variant.templateTypeName,
        )
        assertNotEmpty(inheritedVariantType)

        if (
          isEqualOrSuperType({
            evaluatedTypeName: variantTypeName,
            modelMetaTypeName: variant.templateTypeName,
            metaModel,
          })
        ) {
          assertNotEmpty(variant.name)
          parentOptions.add(variant.name)
        }
      })

    return Array.from(parentOptions).sort()
  }, [
    availableSiblings,
    inheritedFieldsWithVariants,
    metaModel,
    variant.subTemplateIdentifier,
    variant.typeName,
    variantTypeName,
  ])
}
